import Facebook from 'components/Facebook';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { ReactNode, useEffect, useState } from 'react';
import { Form, Input, Message } from 'semantic-ui-react';
import { scrollToSection, sleep } from 'utils/common';
import RoundedButton from '../RoundedButton';

import * as Sentry from '@sentry/browser';
import Apple from 'components/Apple';
import Google from 'components/Google';
import { useABTestStore } from 'hooks/stores/useABTestStore';
import { useUIStore } from 'hooks/stores/useUIStore';
import { useUserStore } from 'hooks/stores/useUserStore';
import { useIsMobile } from 'hooks/ui/useIsMobile';
import { observer } from 'mobx-react-lite';
import { importAbTestVariantsIntoLocalStorage } from 'utils/analytics/abtest';
import { identifyUser, trackEvent } from 'utils/analytics/analytics';
import { authApiClient } from 'utils/apiClient';
import { APP_ENDPOINT } from 'utils/constants';
import { setCookie } from 'utils/cookie';
import HttpStatusCode from 'utils/HttpStatusCode';
import { isFacebookApp } from 'utils/ua';

interface LoginFormProps {
  completionHandler: (token: string) => void;
  submitButtonTitle?: string;
  appleRedirectPath: string;
  noReset?: string;
  elementBeforeLoginButtons?: ReactNode;
  socialButtonStyle?: 'DEFAULT' | 'CIRCLE_ON_MOBILE' | undefined;
  noStyleOnSignInWithEmailPrompt?: boolean;
  showRequiredFieldIndicators?: boolean;
}

const LoginForm = observer(
  ({
    completionHandler,
    submitButtonTitle,
    appleRedirectPath,
    noReset = undefined,
    elementBeforeLoginButtons,
    socialButtonStyle,
    noStyleOnSignInWithEmailPrompt: noStyleOnSignInWithEmailPromopt,
    showRequiredFieldIndicators,
  }: LoginFormProps) => {
    const router = useRouter();
    const { isMobile } = useIsMobile();
    const { updateCurrentUser } = useUserStore();
    const { displayToast } = useUIStore();
    const abTestStore = useABTestStore();

    const [email, setEmail] = useState<string>('');
    const [password, setPassword] = useState<string>('');

    const [showPassword, setShowPassword] = useState<boolean>(false);

    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    const [hideGoogleLogin, setHideGoogleLogin] = useState(false);
    const [hideAppleLogin, setHideAppleLogin] = useState(false);

    useEffect(() => {
      if (!router.isReady) return;
      const result = isFacebookApp(isMobile);

      setHideGoogleLogin(result);
      setHideAppleLogin(result);
    }, [router.isReady, isMobile]);

    const handleSuccessfulLogin = async (
      token: string,
      welcomeName: string,
      abTestVariants: Record<string, string> | null | undefined,
    ) => {
      setCookie('jwt', token, { secure: true, expires: 30 });
      const user = await updateCurrentUser();

      if (!user) {
        return;
      }

      trackEvent('Login');
      identifyUser({
        id: user.id,
        emailAddress: user.emailAddress,
        phoneNumber: user.phoneNumber || undefined,
        firstName: user.firstName,
        lastName: user.lastName,
      });

      displayToast({
        title: welcomeName
          ? `Welcome back to DAYMADE, ${welcomeName}!`
          : 'Welcome back to DAYMADE!',
        timeout: 3500,
      });

      if (abTestVariants) {
        importAbTestVariantsIntoLocalStorage(abTestVariants);
        abTestStore.updateABTestValuesFromLocalStorage();
      }
    };

    const loginButtonHandler = async (e: React.FormEvent) => {
      e.preventDefault();
      setLoading(true);

      try {
        const res = await authApiClient.public.login({
          body: {
            email: email.trim(),
            password: password,
          },
        });

        if (res.status === HttpStatusCode.OK) {
          await handleSuccessfulLogin(
            res.body.token,
            res.body.player.firstName,
            res.body.player.abtestVariants,
          );
          await completionHandler(res.body.token);
          return;
        }

        if (res.status === HttpStatusCode.UNAUTHORIZED) {
          setError(res.body.message);
        }
      } catch (error) {
        setError(
          'Something went wrong. Please email us if this issue persists: hello@daymade.co.uk',
        );
      }

      await sleep(100); // Needed to ensure the error has already rendered.
      await scrollToSection('#errorMessage');

      setLoading(false);
    };

    //TODO: Generic handle these error cases
    // switch (res.body) {
    //   case 'GUEST_ACCOUNT':
    //     localStorage.setItem('guest_email_address', res.emailAddress);
    //     await router.push('/register');

    //   case 'ACCOUNT_CLOSED':
    //     setError(
    //       'This account has been closed. If you have any questions, you can email them to: hello@daymade.co.uk.'
    //     );
    //     break;

    //   case '2FA_REQUIRED':
    //     setError('This account must login using google sign in');
    //     break;

    //   case 'INVALID_CREDENTIALS':
    //     setError(
    //       'The email address and password you provided do not match our records.'
    //     );
    //     break;

    //   case 'FACEBOOK_LINKED_ACCOUNT':
    //     setError(
    //       'You have attempted to log into an DAYMADE account that was created using the Continue With Facebook button. Please sign in using the Continue With Facebook button.'
    //     );
    //     break;

    //   default:
    //     setError(
    //       'Something went wrong. Please email us if this issue persists: hello@daymade.co.uk'
    //     );
    //     break;
    // }

    return (
      <div className="loginForm fullWidth flexCentered">
        {elementBeforeLoginButtons}
        <div
          className={
            'loginFormSocialButtons ' +
            (socialButtonStyle == 'CIRCLE_ON_MOBILE'
              ? ''
              : 'loginFormSocialButtonsColumn')
          }
        >
          <Facebook
            socialButtonStyle={socialButtonStyle}
            scope="email"
            // scope='email,user_birthday,user_gender' // TODO: Consider uncommenting if we want to retro-fill gender/dob data for already registered users.
            completionHandler={async (response) => {
              if (!response.accessToken || !response.userID) {
                Sentry.captureMessage(
                  `Facebook login failed no response.accessToken ${response.accessToken} or response.userID ${response.userID}`,
                );
                return;
              }

              try {
                const result = await authApiClient.public.loginWithFacebook({
                  body: {
                    accessToken: response.accessToken,
                  },
                });

                if (result.status === HttpStatusCode.OK) {
                  await handleSuccessfulLogin(
                    result.body.token,
                    result.body.welcomeName,
                    result.body.abtestVariants,
                  );
                  await completionHandler(result.body.token);
                } else if (result.status === HttpStatusCode.UNAUTHORIZED) {
                  setError(result.body.message);

                  await sleep(100); // Needed to ensure the error has already rendered.
                  await scrollToSection('#errorMessage');
                }
              } catch (error) {
                Sentry.captureMessage(`loginResponse not successful ${error}`);

                setError(
                  error instanceof Error ? error.message : 'An error occurred',
                );
              }
            }}
            style={{
              marginTop: '1em',
            }}
          />

          {!hideGoogleLogin && (
            <Google
              socialButtonStyle={socialButtonStyle}
              onSuccess={async (response) => {
                const result = await authApiClient.public.loginWithGoogle({
                  body: {
                    accessToken: response.access_token,
                  },
                });

                if (result.status === HttpStatusCode.OK) {
                  await handleSuccessfulLogin(
                    result.body.token,
                    result.body.welcomeName,
                    result.body.abtestVariants,
                  );
                  await completionHandler(result.body.token);
                } else {
                  console.log('google login failed', result.body.message);

                  setError(result.body.message);
                  await sleep(100); // Needed to ensure the error has already rendered.
                  await scrollToSection('#errorMessage');
                }
              }}
              onError={async (error) => {
                console.log('google login onError', error);
              }}
              onNonOAuthError={async (error) => {
                console.log('google login NonOAuthError', error);
              }}
            />
          )}
          {!hideAppleLogin && (
            <Apple
              socialButtonStyle={socialButtonStyle}
              redirectURI={`https://${APP_ENDPOINT}${appleRedirectPath}`}
              onSuccess={async (appleUser) => {
                const { status, body } =
                  await authApiClient.public.loginWithApple({
                    body: {
                      accessToken: appleUser.authorization.id_token,
                    },
                  });

                if (status === HttpStatusCode.OK) {
                  await handleSuccessfulLogin(
                    body.token,
                    body.welcomeName,
                    body.abtestVariants,
                  );
                  await completionHandler(body.token);
                } else {
                  setError(body.message);
                }
              }}
            />
          )}
        </div>
        <p
          style={
            noStyleOnSignInWithEmailPromopt
              ? undefined
              : { marginTop: '2em', fontWeight: 700, fontSize: '1.3em' }
          }
        >
          Or log in with email
        </p>
        <Form id="form" onSubmit={loginButtonHandler}>
          <Form.Field>
            <Input
              id="emailField"
              placeholder={
                'Email address' + (showRequiredFieldIndicators ? '*' : '')
              }
              type="text"
              autoComplete="username"
              autoCorrect="off"
              onChange={(e) => {
                setEmail(e.target.value);
                setError('');
              }}
              value={email}
            />
          </Form.Field>

          <Form.Field>
            <Input
              icon={{
                name: showPassword ? 'hide' : 'unhide',
                link: true,
                onClick: () => {
                  setShowPassword(!showPassword);
                },
              }}
              placeholder={
                'Password' + (showRequiredFieldIndicators ? '*' : '')
              }
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              onChange={(e) => {
                setPassword(e.target.value);
                setError('');
              }}
              value={password}
            />
          </Form.Field>

          <input
            type="submit"
            style={{ visibility: 'hidden', position: 'absolute' }}
          />

          <RoundedButton
            onClick={loginButtonHandler}
            disabled={!email || !password}
            loading={loading}
            className="loginButton"
          >
            {submitButtonTitle ?? 'LOGIN'}
          </RoundedButton>

          {error && error.length > 0 && (
            <>
              <div style={{ height: '20px' }} />
              <Message
                id="errorMessage"
                negative
                header="Whoops..."
                content={error}
              />
            </>
          )}

          {!noReset && (
            <>
              <Link href="/password-reset">
                <div
                  style={{
                    marginTop: '20px',
                    fontSize: '14px',
                    textAlign: 'center',
                  }}
                >
                  Forgotten your password?
                </div>
              </Link>

              <Link
                href="/password-reset"
                style={{
                  fontWeight: 'bold',
                  fontSize: '13px',
                  textDecoration: 'underline',
                }}
              >
                Reset password
              </Link>
            </>
          )}
        </Form>
        <style jsx>
          {`
            :global(.loginForm input) {
              border-radius: 25px !important;
            }

            .loginFormSocialButtons {
              display: flex;
              flex-direction: row;
              gap: 10px;
              justify-content: center;
              align-items: center;
            }
            .loginFormSocialButtons.loginFormSocialButtonsColumn {
              width: 100%;
              flex-direction: column;
            }
            @media (min-width: 768px) {
              .loginFormSocialButtons {
                width: 100%;
                flex-direction: column;
              }
            }
          `}
        </style>
      </div>
    );
  },
);

export default LoginForm;
